/**
 * Gift-fund landing state: ?status=, scroll dock, and the footer action.
 *
 * Belongs here: status, tracking, and CTA behaviour.
 * Does not belong here: layout or copy markup.
 */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useUserDetails } from '../../../hooks/useUserDetails'
import { EventNames } from '../../../types/misc.types'
import { goBack, lightImpactVibrate, openMail, openWhatsapp, trackEvent } from '../../../utils/appBridge'
import { buildPpfasGiftFundEnquiryMailTarget, MAIL_SVG } from '../bottom-sheet/PpfasGiftFundNotOnboardedBottomSheet'
import { PPFAS_GIFT_FUND_WHATSAPP_URL, WHATSAPP_SVG } from '../bottom-sheet/PpfasGiftFundOnboardedBottomSheet'
import { PPFAS_GIFT_FUND_ISIN } from '../usePpfasGiftFundHandling'

const SCREEN_NAME = 'PPFAS_GIFT_FUND_LANDING'

export enum PpfasGiftFundLandingStatus {
  NOT_PRIME = 'NOT_PRIME',
  NOT_ONBOARDED = 'NOT_ONBOARDED',
  ONBOARDED = 'ONBOARDED'
}

/** Status from the URL, plus back and the footer action. */
export function usePpfasGiftFundLanding() {
  const [searchParams] = useSearchParams()
  const status = landingStatus(searchParams.get('status'))
  const { userDetails } = useUserDetails()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [ctaDocked, setCtaDocked] = useState(false)
  const showCta = status !== PpfasGiftFundLandingStatus.NOT_PRIME
  const ctaLabel = status === PpfasGiftFundLandingStatus.ONBOARDED ? 'Invest now' : 'Send investment request'
  const ctaIcon = status === PpfasGiftFundLandingStatus.ONBOARDED ? WHATSAPP_SVG : MAIL_SVG

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: SCREEN_NAME,
        eventId: SCREEN_NAME,
        fundIsin: PPFAS_GIFT_FUND_ISIN,
        screenType: status
      }
    })
  }, [status])

  useEffect(() => {
    const scrollNode = scrollRef.current
    if (!scrollNode) {
      return
    }

    // Top border only once the list has been scrolled to the end.
    function syncCtaDock() {
      const node = scrollRef.current
      if (!node) {
        return
      }
      const remaining = node.scrollHeight - node.scrollTop - node.clientHeight
      setCtaDocked(node.scrollTop > 0 && remaining <= 2)
    }

    syncCtaDock()
    scrollNode.addEventListener('scroll', syncCtaDock, { passive: true })
    const observer = new ResizeObserver(syncCtaDock)
    observer.observe(scrollNode)
    if (scrollNode.firstElementChild) {
      observer.observe(scrollNode.firstElementChild)
    }
    return () => {
      scrollNode.removeEventListener('scroll', syncCtaDock)
      observer.disconnect()
    }
  }, [])

  function handleBack() {
    lightImpactVibrate()
    goBack()
  }

  function handleCta() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: SCREEN_NAME,
        eventId: 'PPFAS_GIFT_FUND_LANDING_CTA',
        buttonLabel: ctaLabel,
        fundIsin: PPFAS_GIFT_FUND_ISIN,
        screenType: status
      }
    })
    if (status === PpfasGiftFundLandingStatus.ONBOARDED) {
      openWhatsapp(PPFAS_GIFT_FUND_WHATSAPP_URL)
      return
    }
    openMail(buildPpfasGiftFundEnquiryMailTarget(userDetails?.name ?? '', userDetails?.mobileNumber ?? ''))
  }

  return { scrollRef, ctaDocked, showCta, ctaLabel, ctaIcon, handleBack, handleCta }
}

function landingStatus(status: string | null): PpfasGiftFundLandingStatus {
  if (status === PpfasGiftFundLandingStatus.NOT_ONBOARDED) {
    return PpfasGiftFundLandingStatus.NOT_ONBOARDED
  }
  if (status === PpfasGiftFundLandingStatus.ONBOARDED) {
    return PpfasGiftFundLandingStatus.ONBOARDED
  }
  return PpfasGiftFundLandingStatus.NOT_PRIME
}
